import { useMemo } from "react"
import { Table, Tag } from "antd"
import type { ColumnsType } from "antd/es/table"
import type { Key } from "react"
import { User, UserTableProps, UserStatus } from "../types"
import { UserStatusLabels, UserStatusColors } from "../constants"
import { TableActionColumn } from "@/components/TableActionColumn"
import { ID } from "@/types/common"
import dayjs from "dayjs"

const UserTable = ({
  users,
  onEdit,
  onDelete,
  loading = false,
  selectedIds = [],
  onSelectionChange,
}: UserTableProps) => {
  const columns: ColumnsType<User> = useMemo(
    () => [
      {
        title: "用户编码",
        dataIndex: "code",
        key: "code",
        sorter: (a: User, b: User) => a.code.localeCompare(b.code),
      },
      {
        title: "用户名",
        dataIndex: "name",
        key: "name",
        sorter: (a: User, b: User) => a.name.localeCompare(b.name, "zh"),
      },
      {
        title: "状态",
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (status: UserStatus) => {
          return (
            <Tag color={UserStatusColors[status] || "default"}>
              {UserStatusLabels[status]}
            </Tag>
          )
        },
        filters: [
          { text: "激活", value: UserStatus.ACTIVE },
          { text: "未激活", value: UserStatus.INACTIVE },
        ],
        onFilter: (value: boolean | Key, record: User) => record.status === value,
      },
      {
        title: "角色",
        dataIndex: "roleNames",
        key: "roles",
        render: (roleNames: string[]) => {
          if (!roleNames || roleNames.length === 0) return "-"
          return (
            <>
              {roleNames.map((name, index) => (
                <Tag key={index} color="blue">
                  {name}
                </Tag>
              ))}
            </>
          )
        },
      },
      {
        title: "创建时间",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 180,
        render: (date: string) =>
          date ? dayjs(date).format("YYYY-MM-DD HH:mm:ss") : "-",
        sorter: (a: User, b: User) => {
          if (!a.createdAt || !b.createdAt) return 0
          return dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix()
        },
      },
      TableActionColumn<User>({
        onEdit,
        onDelete: (record: User | ID) => {
          const id =
            typeof record === "object" && record !== null
              ? (record as User).id
              : record
          onDelete(id)
        },
        getId: (record) => record.id,
        deleteConfirmDescription: (record) =>
          `确定要删除用户 "${record.name}" 吗？`,
        width: 200,
      }) as any,
    ],
    [onEdit, onDelete]
  )

  const rowSelection = onSelectionChange
    ? {
        selectedRowKeys: selectedIds,
        onChange: (selectedRowKeys: Key[]) => {
          onSelectionChange(selectedRowKeys as ID[])
        },
        getCheckboxProps: (record: User) => ({
          name: record.code,
        }),
      }
    : undefined

  return (
    <Table
      columns={columns}
      dataSource={users}
      rowKey="id"
      loading={loading}
      rowSelection={rowSelection}
      pagination={{
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        pageSizeOptions: ["10", "20", "50", "100"],
      }}
      size="middle"
      showSorterTooltip={false}
    />
  )
}

export default UserTable
